import { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from '../../../lib/store';
import { isUserVerifiedStatus } from '../../../lib/store/selectors';

import iconDownBlue from '../../../assets/img/icon-sort-down_blue_24px.svg';
import iconDownGray from '../../../assets/img/icon-sort-down_gray_24px.svg';
import iconUpBlue from '../../../assets/img/icon-sort-up_blue_24px.svg';
import iconUpGray from '../../../assets/img/icon-sort-up_gray_24px.svg';
import { useIsMobile } from '../../../hooks';
import BaseTable from '../../BaseTable/BaseTable';
import ClickableImage from '../../ClickableImage/ClickableImage';
import { WATCH_LIST_TABLE_HEADERS } from '../WatchList.constans';
import { WatchListHeaderEnum, WatchListSortedStocks, WatchListStocks } from '../WatchList.types';

import SkeletonLoaderRow from './SkeletonLoaderRow/SkeletonLoaderRow';
import WatchListRow from './WatchlListRow';

import styles from './WatchListTable.module.scss';

type Props = {
  isLoading?: boolean;
  watchListStocks: WatchListStocks[];
  hasSwipeableRows?: boolean;
} & ReduxProps;

const WatchListTable = ({
  isLoading,
  watchListStocks,
  transactions,
  isUserVerified,
  hasSwipeableRows,
}: Props) => {
  const { isMobile } = useIsMobile();
  const [isAscendingOrderLastPrice, setIsAscendingOrderLastPrice] = useState<null | boolean>(null);
  const [isAscendingOrderChange, setIsAscendingOrderChange] = useState<null | boolean>(null);
  const [isAscendingOrderPercentChange, setIsAscendingOrderPercentChange] = useState<null | boolean>(null);
  const [isSortedStocks, setIsSortedStocks] = useState<boolean>(false);
  const [sortedStocks, setSortedStocks] = useState<WatchListSortedStocks[]>([]);
  const [swipedRow, setSwipedRow] = useState <string>('');
  const [tempDataStocks, setTempDataStocks] = useState<WatchListSortedStocks[]>([]);

  // Function to update a specific stock in tempDataStocks
  const updateTempDataStocks = (updatedStock: WatchListSortedStocks) => {
    setTempDataStocks(prevData => {
      // Filter out stocks not in watchListStocks
      const filteredData = prevData.filter(stock =>
        watchListStocks.some(watchListStock => watchListStock.symbol === stock.symbol));

      // Add or update the stock
      const stockIndex = filteredData.findIndex(stock => stock.symbol === updatedStock.symbol);
      if (stockIndex !== -1) {
        const newData = [...filteredData];
        newData[stockIndex] = updatedStock;
        return newData;
      }

      const newData = [...filteredData, updatedStock];

      // Ensure length does not exceed watchListStocks.length
      if (newData.length > watchListStocks.length) {
        return newData.slice(-watchListStocks.length);
      }

      return newData;
    });
  };

  const handleIconUp = (item: number) => {
    switch (item) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        if (isAscendingOrderLastPrice === null) {
          return iconUpGray;
        }
        return isAscendingOrderLastPrice ? iconUpBlue : iconUpGray;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        if (isAscendingOrderChange === null) {
          return iconUpGray;
        }
        return isAscendingOrderChange ? iconUpBlue : iconUpGray;
      default:
        if (isAscendingOrderPercentChange === null) {
          return iconUpGray;
        }
        return isAscendingOrderPercentChange ? iconUpBlue : iconUpGray;
    }
  };

  const handleIconDown = (item: number) => {
    switch (item) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        if (isAscendingOrderLastPrice === null) {
          return iconDownGray;
        }
        return !isAscendingOrderLastPrice ? iconDownBlue : iconDownGray;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        if (isAscendingOrderChange === null) {
          return iconDownGray;
        }
        return !isAscendingOrderChange ? iconDownBlue : iconDownGray;
      default:
        if (isAscendingOrderPercentChange === null) {
          return iconDownGray;
        }
        return !isAscendingOrderPercentChange ? iconDownBlue : iconDownGray;
    }
  };

  const handleChangeUpStates = (currentItem: number) => {
    setIsSortedStocks(true);
    let sortedData: WatchListSortedStocks[] = [];

    // Sort current data in ascending order.
    switch (currentItem) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => a.currentPrice - b.currentPrice);
        setSortedStocks(sortedData);
        setIsAscendingOrderLastPrice(true);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderPercentChange(null);
        break;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => a.change - b.change);
        setSortedStocks(sortedData);
        setIsAscendingOrderChange(true);
        setIsAscendingOrderLastPrice(null);
        setIsAscendingOrderPercentChange(null);
        break;
      default:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => a.changePercent - b.changePercent);
        setSortedStocks(sortedData);
        setIsAscendingOrderPercentChange(true);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderLastPrice(null);
    }
  };

  // TODO: Consider separating interaction - sorting and tab changing - from price updates
  //       See StockList.tsx in Mobile and future updates in usage of TradePriceCache and NBBOPriceCache
  const handleChangeDownStates = (currentItem: number) => {
    setIsSortedStocks(true);
    let sortedData: WatchListSortedStocks[] = [];

    // Sort current data in descending order.
    switch (currentItem) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => b.currentPrice - a.currentPrice);
        setSortedStocks(sortedData);
        setIsAscendingOrderLastPrice(false);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderPercentChange(null);
        break;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => b.change - a.change);
        setSortedStocks(sortedData);
        setIsAscendingOrderChange(false);
        setIsAscendingOrderLastPrice(null);
        setIsAscendingOrderPercentChange(null);
        break;
      default:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => b.changePercent - a.changePercent);
        setSortedStocks(sortedData);
        setIsAscendingOrderPercentChange(false);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderLastPrice(null);
    }
  };

  const tableHeaders = () => WATCH_LIST_TABLE_HEADERS.map((item: string, index) => {
    const handleStyleIconUp = (headerItem: number) => {
      if (
        (isAscendingOrderLastPrice && headerItem === WatchListHeaderEnum.COLUMN_LAST_PRICE)
        || (isAscendingOrderChange && headerItem === WatchListHeaderEnum.COLUMN_CHANGE)
        || (isAscendingOrderPercentChange && headerItem === WatchListHeaderEnum.COLUMN_PERCENT_CHANGE)) {
        return styles.iconUpActive;
      }
      return styles.iconUp;
    };

    const renderHeaderSortItems = (headerItem: number) => (
      <th key={item}>
        <span className={
            (
              (isAscendingOrderLastPrice !== null && headerItem === WatchListHeaderEnum.COLUMN_LAST_PRICE)
              || (isAscendingOrderChange !== null && headerItem === WatchListHeaderEnum.COLUMN_CHANGE)
              || (isAscendingOrderPercentChange !== null && headerItem === WatchListHeaderEnum.COLUMN_PERCENT_CHANGE))
              ? styles.activeHeaderText
              : ''
            }
        >
          {item}
        </span>
        <div className={styles.wrapperIcons}>
          <div className={styles.icons}>
            <ClickableImage
              alt=""
              src={handleIconUp(headerItem)}
              className={handleStyleIconUp(headerItem)}
              onClick={() => handleChangeUpStates(headerItem)}
            />
            <ClickableImage
              alt=""
              src={handleIconDown(headerItem)}
              className={styles.iconDown}
              onClick={() => handleChangeDownStates(headerItem)}
            />
          </div>
        </div>
      </th>
    );

    switch (index) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        return renderHeaderSortItems(WatchListHeaderEnum.COLUMN_LAST_PRICE);
      case WatchListHeaderEnum.COLUMN_CHANGE:
        return renderHeaderSortItems(WatchListHeaderEnum.COLUMN_CHANGE);
      case WatchListHeaderEnum.COLUMN_PERCENT_CHANGE:
        return renderHeaderSortItems(WatchListHeaderEnum.COLUMN_PERCENT_CHANGE);

      default: return <th key={item}>{item}</th>;
    }
  });

  const getHeight = () => {
    if (!isUserVerified && transactions.length === 0) {
      return 320;
    }
    if (isUserVerified && transactions.length === 0) {
      return 260;
    }
    return 180;
  };

  return (
    <div
      className={isMobile ? styles.wrapperMobile : styles.wrapper}
      style={isMobile ? { maxHeight: window.innerHeight - getHeight() } : {}}
    >
      <BaseTable isDiscoverTable>
        {isMobile ? null : (
          <thead>
            <tr>{tableHeaders()}</tr>
          </thead>
        )}
        { isLoading ? (
          <tbody>
            {Array.from({ length: 10 }, (_, index) => (
              <SkeletonLoaderRow key={index} />
            ))}
          </tbody>
        ) : (
          <tbody>
            {
              (isSortedStocks ? sortedStocks : watchListStocks).map(
                stock => (
                  <WatchListRow
                    key={stock.symbol}
                    stock={stock}
                    hasSwipeableRows={hasSwipeableRows}
                    swipedRow={swipedRow}
                    setSwipedRow={setSwipedRow}
                    tempDataStocks={tempDataStocks}
                    updateTempDataStocks={updateTempDataStocks}
                  />
                ),
              )
            }
          </tbody>
        )}
      </BaseTable>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  companyLogosAndNames: state.fundamental.companyLogoAndName,
  transactions: state.payment.paymentsAll.data,
  isUserVerified: isUserVerifiedStatus(state.crm.individualExtendedInfo),
  individual: state.crm.individualExtendedInfo,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(WatchListTable);
